<template>
  <div>
    <!-- Search Bar -->
    <div class="bg-white rounded-lg shadow-sm p-4 w-full mb-4">
      <div class="flex flex-wrap items-center justify-between gap-2 p-4">
        <div class="w-64">
          <SearchBarComponent
            :data="companies"
            search-key="companyName"
            @filtered="handleSearchFilter"
          />
        </div>
        <div class="ml-auto">
          <Button
            icon="pi pi-plus"
            label="Add Company"
            class="p-button-sm"
            severity="secondary"
            raised
            @click="openAddModal"
          />
        </div>
      </div>
    </div>

    <!-- Alphabet Filter -->
    <div class="bg-white rounded-lg shadow-sm p-4 w-full mb-4">
      <AlphabetFilterComponent 
        :data="companies" 
        filter-key="company_name" 
        @filtered="handleLetterFilter"
      />
    </div>

    <!-- Companies Card Layout -->
    <div class="bg-white rounded-lg shadow-sm p-4 w-full">
      <!-- Loading state -->
      <div
        v-if="companyStore.loading"
        class="flex justify-center items-center py-12"
      >
        <ProgressSpinner />
      </div>

      <!-- Error state -->
      <div
        v-else-if="companyStore.error"
        class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4"
      >
        <div class="flex items-center">
          <i class="pi pi-exclamation-triangle text-red-500 mr-2" />
          <span class="text-red-700">Error loading companies: {{ companyStore.error }}</span>
        </div>
      </div>

      <!-- Empty state message when no companies match filters -->
      <div
        v-else-if="filteredCompanies.length === 0"
        class="text-center py-8"
      >
        <i class="pi pi-building text-5xl text-gray-300 mb-3" />
        <p class="text-gray-500">
          No companies match your criteria
        </p>
      </div>

      <!-- Card Layout -->
      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        <div
          v-for="company in filteredCompanies"
          :key="company.id"
          class="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden cursor-pointer"
          @click="viewCompany(company.id)"
        >
          <!-- Company Header with Logo -->
          <div
            class="h-40 relative flex items-center justify-center"
            :style="{ backgroundColor: getRandomColor(company.id) }"
          >
            <!-- Logo image or placeholder -->
            <div
              class="w-24 h-24 rounded-full overflow-hidden border-4 border-white flex items-center justify-center"
            >
              <img
                v-if="company.image_url || company.logo_url || company.logo_path"
                :src="company.image_url || company.logo_url || company.logo_path"
                :alt="company.company_name"
                class="w-full h-full object-cover"
              >
              <div
                v-else
                class="w-full h-full flex items-center justify-center bg-gray-400"
              >
                <i class="pi pi-building text-white text-4xl" />
              </div>
            </div>

            <!-- Company status label -->
            <div
              v-if="company.company_status"
              class="absolute bottom-2 left-2 bg-white bg-opacity-90 px-3 py-1 rounded-lg text-sm font-medium"
            >
              {{ company.company_status }}
            </div>
          </div>

          <!-- Company Details -->
          <div class="p-4">
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              {{ company.company_name }}
            </h3>

            <div
              v-if="company.contact_email"
              class="flex items-center text-sm text-gray-600 mb-1"
            >
              <i class="pi pi-envelope mr-2" />
              <span class="truncate">{{ company.contact_email }}</span>
            </div>

            <div
              v-if="company.phone_number"
              class="flex items-center text-sm text-gray-600 mb-1"
            >
              <i class="pi pi-phone mr-2" />
              <span>{{ company.phone_number }}</span>
            </div>

            <div
              v-if="company.website"
              class="flex items-center text-sm text-gray-600 mb-1"
            >
              <i class="pi pi-globe mr-2" />
              <span class="truncate">{{ company.website }}</span>
            </div>
          </div>

          <!-- Action Footer -->
          <div class="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
            <div>
              <!-- Status indicator if applicable -->
              <span
                v-if="company.company_status"
                class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full"
              >
                {{ company.company_status }}
              </span>
            </div>
            <Button
              icon="pi pi-eye"
              class="p-button-rounded p-button-text p-button-sm"
              @click.stop="viewCompany(company.id)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Reusable Dialog for Add Company -->
    <Dialog
      v-model:visible="isDialogVisible"
      :header="dialogHeader"
      :style="{ width: '70vw', maxWidth: '1200px' }"
      :modal="true"
      :closable="true"
    >
      <CompanyModal
        :data="selectedCompany"
        :type="modalType"
        @company-created="handleDialogSubmit"
        @add="handleDialogSubmit"
      />
    </Dialog>
  </div>
</template>

<script setup>
//Pinia Stores
import { useCompanyStore } from '@/stores/companyStore';
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import CompanyModal from '@/components/contacts/modals/CompanyModal.vue';
import SearchBarComponent from '@/components/shared/tableSearch/TableSearch.vue';
import AlphabetFilterComponent from '@/components/shared/alphabetFilter/AlphabetFilterComponent.vue';

// Initialize router for navigation
const router = useRouter();

//Company Store
const companyStore = useCompanyStore();
const companies = computed(() => companyStore.companies);

// Dialog state
const isDialogVisible = ref(false);
const dialogHeader = ref('Company Details');
const modalType = ref('add');
const selectedCompany = ref({});

// Filtering state
const searchFilteredCompanies = ref([]);
const letterFilteredCompanies = ref([]);
const filteredCompanies = ref([]);

// Apply combined filters
const applyFilters = () => {
    if (letterFilteredCompanies.value.length === companies.value.length) {
        // If letter filter is not active, just use search results
        filteredCompanies.value = searchFilteredCompanies.value;
    } else if (searchFilteredCompanies.value.length === companies.value.length) {
        // If search is not active, just use letter filter
        filteredCompanies.value = letterFilteredCompanies.value;
    } else {
        // Both filters active - find intersection
        filteredCompanies.value = searchFilteredCompanies.value.filter(company =>
            letterFilteredCompanies.value.some(c => c.id === company.id)
        );
    }
};

// Handle filtered results from search
const handleSearchFilter = (filtered) => {
    searchFilteredCompanies.value = filtered;
    applyFilters();
};

// Handle filtered results from alphabet (client-side)
const handleLetterFilter = (filtered) => {
    letterFilteredCompanies.value = filtered;
    applyFilters();
};

// Initialize filters when component mounts
onMounted(async () => {
    // Load companies from API
    await companyStore.loadCompanies();
    
    // Initialize filtered companies - ensure companies is an array
    const companiesArray = Array.isArray(companies.value) ? companies.value : [];
    searchFilteredCompanies.value = [...companiesArray];
    letterFilteredCompanies.value = [...companiesArray];
    applyFilters();
});

// Watch for changes in companies data
watch(companies, (newCompanies) => {
    // Ensure newCompanies is an array before spreading
    const companiesArray = Array.isArray(newCompanies) ? newCompanies : [];
    searchFilteredCompanies.value = [...companiesArray];
    letterFilteredCompanies.value = [...companiesArray];
    applyFilters();
}, { immediate: true });

// Navigate to view a specific company
const viewCompany = (id) => {
    router.push(`/view-company/${id}`);
};

// Function to generate a random color based on company ID
const getRandomColor = (id) => {
    const colors = [
        '#FF6B6B', // Coral Red
        '#4ECDC4', // Turquoise
        '#45B7D1', // Sky Blue
        '#96CEB4', // Sage Green
        '#FFEEAD', // Light Yellow
        '#D4A5A5', // Dusty Rose
        '#9B59B6', // Purple
        '#3498DB', // Blue
        '#E67E22', // Orange
        '#2ECC71', // Green
        '#F1C40F', // Yellow
        '#E74C3C', // Red
        '#1ABC9C', // Teal
        '#34495E', // Dark Blue
        '#F39C12', // Gold
    ];
    // Use the company ID to get a consistent color for each company
    return colors[id % colors.length];
};

// Open dialog to add a new company
const openAddModal = () => {
    selectedCompany.value = {};
    modalType.value = 'add';
    dialogHeader.value = 'Add Company';
    isDialogVisible.value = true;
};

// Handle dialog submit for both add and view
const handleDialogSubmit = async () => {
    if (modalType.value === 'add') {
        // Reload companies list to show the new company
        await companyStore.loadCompanies();
    }
    isDialogVisible.value = false;
};
</script>

<style scoped>
.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
}
</style>